import { Component, forwardRef, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import getUnicodeFlagIcon from 'country-flag-icons/unicode'
interface Country {
  value: string;
  label: string;
}

/**
 * WIPO, patent and trademark offices. They are part of the backend CountryCode enum because
 * trademarks are filed against them, but they are never postal addresses.
 */
export const IP_JURISDICTION_CODES = ['AP', 'BX', 'EF', 'EM', 'EP', 'EV', 'GC', 'IB', 'OA', 'WO'];

/** Unions, dissolved states and CLDR region groupings, which are no postal addresses either. */
export const NON_COUNTRY_REGION_CODES = ['AN', 'EU', 'QO', 'SU', 'ZZ'];

const NON_ADDRESS_CODES = [...IP_JURISDICTION_CODES, ...NON_COUNTRY_REGION_CODES];

/** 'address' offers postal countries only, 'all' the complete CountryCode enum. */
export type CountrySelectorScope = 'address' | 'all';

@Component({
  selector: 'app-country-selector',
  imports: [FormsModule, SelectModule],
  templateUrl: './country-selector.component.html',
  styleUrl: './country-selector.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountrySelectorComponent),
      multi: true
    }
  ]
})
export class CountrySelectorComponent implements ControlValueAccessor {
  @Input() placeholder = $localize`:country selector|placeholder:Select a Country`;

  /** Use 'all' where the IP jurisdictions are valid choices, e.g. trademark jurisdictions. */
  @Input() scope: CountrySelectorScope = 'address';

  selectedCountry: Country | undefined = undefined;
  disabled = false;

  private optionsCache: { key: string, options: Country[] } | undefined = undefined;

  /**
   * The countries offered in the dropdown. Non-address entries are hidden, unless one is the
   * current value - a record stored with such a code has to keep displaying it instead of
   * showing an empty field. Memoized to keep the array identity stable across change detection.
   */
  get options(): Country[] {
    const key = `${this.scope}|${this.selectedCountry?.value ?? ''}`;
    if (this.optionsCache?.key !== key) {
      this.optionsCache = { key, options: this.buildOptions() };
    }
    return this.optionsCache.options;
  }

  private buildOptions(): Country[] {
    if (this.scope === 'all') {
      return this.countries;
    }
    return this.countries.filter(country =>
      !NON_ADDRESS_CODES.includes(country.value) || country.value === this.selectedCountry?.value
    );
  }

  onChanged: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: any): void {
    this.selectedCountry = this.countries.find(c => c.value === value) ?? undefined;
  }

  registerOnChange(fn: (value: any) => void) {
    this.onChanged = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  setCountry(country: Country | undefined) {
    this.selectedCountry = country;
    this.onChanged(country?.value ?? null);
    this.onTouched();
  }

  handleBlur() {
    this.onTouched();
  }

  public getUnicodeFlagIcon = getUnicodeFlagIcon;

  countries = [
    { value: "AC", label: $localize`Ascension Island` },
    { value: "AD", label: $localize`Andorra` },
    { value: "AE", label: $localize`United Arab Emirates` },
    { value: "AF", label: $localize`Afghanistan` },
    { value: "AG", label: $localize`Antigua and Barbuda` },
    { value: "AI", label: $localize`Anguilla` },
    { value: "AL", label: $localize`Albania` },
    { value: "AM", label: $localize`Armenia` },
    { value: "AN", label: $localize`Netherlands Antilles` },
    { value: "AO", label: $localize`Angola` },
    { value: "AQ", label: $localize`Antarctica` },
    { value: "AR", label: $localize`Argentina` },
    { value: "AS", label: $localize`American Samoa` },
    { value: "AT", label: $localize`Austria` },
    { value: "AU", label: $localize`Australia` },
    { value: "AW", label: $localize`Aruba` },
    { value: "AX", label: $localize`Åland Islands` },
    { value: "AZ", label: $localize`Azerbaijan` },
    { value: "BA", label: $localize`Bosnia and Herzegovina` },
    { value: "BB", label: $localize`Barbados` },
    { value: "BD", label: $localize`Bangladesh` },
    { value: "BE", label: $localize`Belgium` },
    { value: "BF", label: $localize`Burkina Faso` },
    { value: "BG", label: $localize`Bulgaria` },
    { value: "BH", label: $localize`Bahrain` },
    { value: "BI", label: $localize`Burundi` },
    { value: "BJ", label: $localize`Benin` },
    { value: "BL", label: $localize`Saint Barthélemy` },
    { value: "BM", label: $localize`Bermuda` },
    { value: "BN", label: $localize`Brunei` },
    { value: "BO", label: $localize`Bolivia` },
    { value: "BQ", label: $localize`Caribbean Netherlands` },
    { value: "BR", label: $localize`Brazil` },
    { value: "BS", label: $localize`Bahamas` },
    { value: "BT", label: $localize`Bhutan` },
    { value: "BV", label: $localize`Bouvet Island` },
    { value: "BW", label: $localize`Botswana` },
    { value: "BY", label: $localize`Belarus` },
    { value: "BZ", label: $localize`Belize` },
    { value: "CA", label: $localize`Canada` },
    { value: "CC", label: $localize`Cocos (Keeling) Islands` },
    { value: "CD", label: $localize`Congo (DRC)` },
    { value: "CF", label: $localize`Central African Republic` },
    { value: "CG", label: $localize`Congo (Republic)` },
    { value: "CH", label: $localize`Switzerland` },
    { value: "CI", label: $localize`Ivory Coast` },
    { value: "CK", label: $localize`Cook Islands` },
    { value: "CL", label: $localize`Chile` },
    { value: "CM", label: $localize`Cameroon` },
    { value: "CN", label: $localize`China` },
    { value: "CO", label: $localize`Colombia` },
    { value: "CP", label: $localize`Clipperton Island` },
    { value: "CR", label: $localize`Costa Rica` },
    { value: "CU", label: $localize`Cuba` },
    { value: "CV", label: $localize`Cape Verde` },
    { value: "CW", label: $localize`Curaçao` },
    { value: "CX", label: $localize`Christmas Island` },
    { value: "CY", label: $localize`Cyprus` },
    { value: "CZ", label: $localize`Czech Republic` },
    { value: "DE", label: $localize`Germany` },
    { value: "DG", label: $localize`Diego Garcia` },
    { value: "DJ", label: $localize`Djibouti` },
    { value: "DK", label: $localize`Denmark` },
    { value: "DM", label: $localize`Dominica` },
    { value: "DO", label: $localize`Dominican Republic` },
    { value: "DZ", label: $localize`Algeria` },
    { value: "EA", label: $localize`Ceuta and Melilla` },
    { value: "EC", label: $localize`Ecuador` },
    { value: "EE", label: $localize`Estonia` },
    { value: "EG", label: $localize`Egypt` },
    { value: "EH", label: $localize`Western Sahara` },
    { value: "ER", label: $localize`Eritrea` },
    { value: "ES", label: $localize`Spain` },
    { value: "ET", label: $localize`Ethiopia` },
    { value: "EU", label: $localize`European Union` },
    { value: "FI", label: $localize`Finland` },
    { value: "FJ", label: $localize`Fiji` },
    { value: "FK", label: $localize`Falkland Islands (Islas Malvinas)` },
    { value: "FM", label: $localize`Micronesia` },
    { value: "FO", label: $localize`Faroe Islands` },
    { value: "FR", label: $localize`France` },
    { value: "GA", label: $localize`Gabon` },
    { value: "GB", label: $localize`United Kingdom` },
    { value: "GD", label: $localize`Grenada` },
    { value: "GE", label: $localize`Georgia` },
    { value: "GF", label: $localize`French Guiana` },
    { value: "GG", label: $localize`Guernsey` },
    { value: "GH", label: $localize`Ghana` },
    { value: "GI", label: $localize`Gibraltar` },
    { value: "GL", label: $localize`Greenland` },
    { value: "GM", label: $localize`Gambia` },
    { value: "GN", label: $localize`Guinea` },
    { value: "GP", label: $localize`Guadeloupe` },
    { value: "GQ", label: $localize`Equatorial Guinea` },
    { value: "GR", label: $localize`Greece` },
    { value: "GS", label: $localize`South Georgia & South Sandwich Islands` },
    { value: "GT", label: $localize`Guatemala` },
    { value: "GU", label: $localize`Guam` },
    { value: "GW", label: $localize`Guinea-Bissau` },
    { value: "GY", label: $localize`Guyana` },
    { value: "HK", label: $localize`Hong Kong` },
    { value: "HM", label: $localize`Heard & McDonald Islands` },
    { value: "HN", label: $localize`Honduras` },
    { value: "HR", label: $localize`Croatia` },
    { value: "HT", label: $localize`Haiti` },
    { value: "HU", label: $localize`Hungary` },
    { value: "IC", label: $localize`Canary Islands` },
    { value: "ID", label: $localize`Indonesia` },
    { value: "IE", label: $localize`Ireland` },
    { value: "IL", label: $localize`Israel` },
    { value: "IM", label: $localize`Isle of Man` },
    { value: "IN", label: $localize`India` },
    { value: "IO", label: $localize`British Indian Ocean Territory` },
    { value: "IQ", label: $localize`Iraq` },
    { value: "IR", label: $localize`Iran` },
    { value: "IS", label: $localize`Iceland` },
    { value: "IT", label: $localize`Italy` },
    { value: "JE", label: $localize`Jersey` },
    { value: "JM", label: $localize`Jamaica` },
    { value: "JO", label: $localize`Jordan` },
    { value: "JP", label: $localize`Japan` },
    { value: "KE", label: $localize`Kenya` },
    { value: "KG", label: $localize`Kyrgyzstan` },
    { value: "KH", label: $localize`Cambodia` },
    { value: "KI", label: $localize`Kiribati` },
    { value: "KM", label: $localize`Comoros` },
    { value: "KN", label: $localize`Saint Kitts and Nevis` },
    { value: "KP", label: $localize`North Korea` },
    { value: "KR", label: $localize`South Korea` },
    { value: "KW", label: $localize`Kuwait` },
    { value: "KY", label: $localize`Cayman Islands` },
    { value: "KZ", label: $localize`Kazakhstan` },
    { value: "LA", label: $localize`Laos` },
    { value: "LB", label: $localize`Lebanon` },
    { value: "LC", label: $localize`Saint Lucia` },
    { value: "LI", label: $localize`Liechtenstein` },
    { value: "LK", label: $localize`Sri Lanka` },
    { value: "LR", label: $localize`Liberia` },
    { value: "LS", label: $localize`Lesotho` },
    { value: "LT", label: $localize`Lithuania` },
    { value: "LU", label: $localize`Luxembourg` },
    { value: "LV", label: $localize`Latvia` },
    { value: "LY", label: $localize`Libya` },
    { value: "MA", label: $localize`Morocco` },
    { value: "MC", label: $localize`Monaco` },
    { value: "MD", label: $localize`Moldova` },
    { value: "ME", label: $localize`Montenegro` },
    { value: "MF", label: $localize`Saint Martin` },
    { value: "MG", label: $localize`Madagascar` },
    { value: "MH", label: $localize`Marshall Islands` },
    { value: "MK", label: $localize`Macedonia (FYROM)` },
    { value: "ML", label: $localize`Mali` },
    { value: "MM", label: $localize`Myanmar (Burma)` },
    { value: "MN", label: $localize`Mongolia` },
    { value: "MO", label: $localize`Macau` },
    { value: "MP", label: $localize`Northern Mariana Islands` },
    { value: "MQ", label: $localize`Martinique` },
    { value: "MR", label: $localize`Mauritania` },
    { value: "MS", label: $localize`Montserrat` },
    { value: "MT", label: $localize`Malta` },
    { value: "MU", label: $localize`Mauritius` },
    { value: "MV", label: $localize`Maldives` },
    { value: "MW", label: $localize`Malawi` },
    { value: "MX", label: $localize`Mexico` },
    { value: "MY", label: $localize`Malaysia` },
    { value: "MZ", label: $localize`Mozambique` },
    { value: "NA", label: $localize`Namibia` },
    { value: "NC", label: $localize`New Caledonia` },
    { value: "NE", label: $localize`Niger` },
    { value: "NF", label: $localize`Norfolk Island` },
    { value: "NG", label: $localize`Nigeria` },
    { value: "NI", label: $localize`Nicaragua` },
    { value: "NL", label: $localize`Netherlands` },
    { value: "NO", label: $localize`Norway` },
    { value: "NP", label: $localize`Nepal` },
    { value: "NR", label: $localize`Nauru` },
    { value: "NU", label: $localize`Niue` },
    { value: "NZ", label: $localize`New Zealand` },
    { value: "OM", label: $localize`Oman` },
    { value: "PA", label: $localize`Panama` },
    { value: "PE", label: $localize`Peru` },
    { value: "PF", label: $localize`French Polynesia` },
    { value: "PG", label: $localize`Papua New Guinea` },
    { value: "PH", label: $localize`Philippines` },
    { value: "PK", label: $localize`Pakistan` },
    { value: "PL", label: $localize`Poland` },
    { value: "PM", label: $localize`Saint Pierre and Miquelon` },
    { value: "PN", label: $localize`Pitcairn Islands` },
    { value: "PR", label: $localize`Puerto Rico` },
    { value: "PS", label: $localize`Palestine` },
    { value: "PT", label: $localize`Portugal` },
    { value: "PW", label: $localize`Palau` },
    { value: "PY", label: $localize`Paraguay` },
    { value: "QA", label: $localize`Qatar` },
    { value: "QO", label: $localize`Outlying Oceania` },
    { value: "RE", label: $localize`Réunion` },
    { value: "RO", label: $localize`Romania` },
    { value: "RS", label: $localize`Serbia` },
    { value: "RU", label: $localize`Russia` },
    { value: "RW", label: $localize`Rwanda` },
    { value: "SA", label: $localize`Saudi Arabia` },
    { value: "SB", label: $localize`Solomon Islands` },
    { value: "SC", label: $localize`Seychelles` },
    { value: "SD", label: $localize`Sudan` },
    { value: "SE", label: $localize`Sweden` },
    { value: "SG", label: $localize`Singapore` },
    { value: "SH", label: $localize`Saint Helena` },
    { value: "SI", label: $localize`Slovenia` },
    { value: "SJ", label: $localize`Svalbard and Jan Mayen` },
    { value: "SK", label: $localize`Slovakia` },
    { value: "SL", label: $localize`Sierra Leone` },
    { value: "SM", label: $localize`San Marino` },
    { value: "SN", label: $localize`Senegal` },
    { value: "SO", label: $localize`Somalia` },
    { value: "SR", label: $localize`Suriname` },
    { value: "SS", label: $localize`South Sudan` },
    { value: "ST", label: $localize`São Tomé and Príncipe` },
    { value: "SU", label: $localize`Soviet Union` },
    { value: "SV", label: $localize`El Salvador` },
    { value: "SX", label: $localize`Sint Maarten` },
    { value: "SY", label: $localize`Syria` },
    { value: "SZ", label: $localize`Swaziland` },
    { value: "TA", label: $localize`Tristan da Cunha` },
    { value: "TC", label: $localize`Turks and Caicos Islands` },
    { value: "TD", label: $localize`Chad` },
    { value: "TF", label: $localize`French Southern Territories` },
    { value: "TG", label: $localize`Togo` },
    { value: "TH", label: $localize`Thailand` },
    { value: "TJ", label: $localize`Tajikistan` },
    { value: "TK", label: $localize`Tokelau` },
    { value: "TL", label: $localize`East Timor` },
    { value: "TM", label: $localize`Turkmenistan` },
    { value: "TN", label: $localize`Tunisia` },
    { value: "TO", label: $localize`Tonga` },
    { value: "TR", label: $localize`Turkey` },
    { value: "TT", label: $localize`Trinidad and Tobago` },
    { value: "TV", label: $localize`Tuvalu` },
    { value: "TW", label: $localize`Taiwan` },
    { value: "TZ", label: $localize`Tanzania` },
    { value: "UA", label: $localize`Ukraine` },
    { value: "UG", label: $localize`Uganda` },
    { value: "UM", label: $localize`U.S. Outlying Islands` },
    { value: "US", label: $localize`U.S.` },
    { value: "UY", label: $localize`Uruguay` },
    { value: "UZ", label: $localize`Uzbekistan` },
    { value: "VA", label: $localize`Vatican City` },
    { value: "VC", label: $localize`St. Vincent & Grenadines` },
    { value: "VE", label: $localize`Venezuela` },
    { value: "VG", label: $localize`British Virgin Islands` },
    { value: "VI", label: $localize`U.S. Virgin Islands` },
    { value: "VN", label: $localize`Vietnam` },
    { value: "VU", label: $localize`Vanuatu` },
    { value: "WF", label: $localize`Wallis and Futuna` },
    { value: "WS", label: $localize`Samoa` },
    { value: "XK", label: $localize`Kosovo` },
    { value: "YE", label: $localize`Yemen` },
    { value: "YT", label: $localize`Mayotte` },
    { value: "ZA", label: $localize`South Africa` },
    { value: "ZM", label: $localize`Zambia` },
    { value: "ZW", label: $localize`Zimbabwe` },
    { value: "ZZ", label: $localize`Unknown Region` },
    { value: "AP", label: $localize`African Regional Industrial Property Organization` },
    { value: "BX", label: $localize`Benelux Trademarks and Design Offices` },
    { value: "EF", label: $localize`Union of Countries under the European Community Patent Convention` },
    { value: "EM", label: $localize`European Trademark Office` },
    { value: "EP", label: $localize`European Patent Organization (Union of countries under the European Patent Convention)` },
    { value: "EV", label: $localize`Eurasian Patent Organization` },
    { value: "GC", label: $localize`Patent Office of the Cooperation Council for the Arab States of the Gulf (GCC)` },
    { value: "IB", label: $localize`International Bureau of WIPO` },
    { value: "OA", label: $localize`African Intellectual Property Organization` },
    { value: "WO", label: $localize`World Intellectual Property Organization` }
  ];
}
